import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import { DragIndicator, Visibility, VisibilityOff, EmojiEvents, Title } from '@mui/icons-material';
import { achievementService } from '../../services/achievementService';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

const BadgeSettings = ({ open, onClose, user, onUpdate }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [badges, setBadges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [titles, setTitles] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({
    showBadges: true,
    maxDisplay: 3,
    visibleBadges: [],
    badgeOrder: [],
    achievementOrder: [],
    titleOrder: []
  });
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    if (open && user) {
      loadUserBadges();
      loadUserAchievements();
      loadUserTitles();
      loadDisplaySettings();
    }
  }, [open, user]);

  const loadUserBadges = async () => {
    try {
      const userBadges = await achievementService.getUserBadges(user.id);
      setBadges(userBadges);
    } catch (error) {
      console.error('Error loading badges:', error);
    }
  };

  const loadUserAchievements = async () => {
    try {
      const userAchievements = await achievementService.getUserAchievements(user.id);
      setAchievements(userAchievements);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const loadUserTitles = () => {
    const userTitles = user.titles || [];
    setTitles(userTitles.map(title => ({ type: title, name: title })));
  };

  const loadDisplaySettings = () => {
    try {
      const settings = user.badgeDisplaySettings 
        ? JSON.parse(user.badgeDisplaySettings)
        : { 
            showBadges: true, 
            maxDisplay: 3, 
            visibleBadges: [],
            badgeOrder: [],
            achievementOrder: [],
            titleOrder: user.titleOrder || []
          };
      setDisplaySettings(settings);
    } catch (error) {
      setDisplaySettings({ 
        showBadges: true, 
        maxDisplay: 3, 
        visibleBadges: [],
        badgeOrder: [],
        achievementOrder: [],
        titleOrder: user.titleOrder || []
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const UPDATE_USER = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            badgeDisplaySettings
            activeBadges
          }
        }
      `;

      console.log('BadgeSettings - Saving settings:', {
        badgeDisplaySettings: displaySettings,
        activeBadges: displaySettings.badgeOrder.length > 0 
          ? displaySettings.badgeOrder 
          : badges.map(b => b.badgeType || b.type),
        badges: badges
      });
      
      const result = await client.graphql({
        query: UPDATE_USER,
        variables: {
          input: {
            id: user.id,
            badgeDisplaySettings: JSON.stringify(displaySettings),
            activeBadges: displaySettings.badgeOrder.length > 0 
              ? displaySettings.badgeOrder 
              : badges.map(b => b.badgeType || b.type),
            titleOrder: displaySettings.titleOrder
          }
        }
      });
      
      console.log('BadgeSettings - Update result:', result.data.updateUser);

      // Pass the updated user data to parent
      onUpdate?.(result.data.updateUser);
      onClose();
    } catch (error) {
      console.error('Error saving badge settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBadgeVisibility = (badgeType) => {
    setDisplaySettings(prev => ({
      ...prev,
      visibleBadges: prev.visibleBadges.includes(badgeType)
        ? prev.visibleBadges.filter(b => b !== badgeType)
        : [...prev.visibleBadges, badgeType]
    }));
  };

  const handleDragStart = (e, item, type) => {
    setDraggedItem({ item, type });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex, type) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== type) return;

    const items = type === 'badge' ? badges : type === 'achievement' ? achievements : titles;
    const draggedIndex = items.findIndex(item => 
      type === 'badge' ? item.id === draggedItem.item.id :
      type === 'achievement' ? item.id === draggedItem.item.id :
      item.type === draggedItem.item.type
    );

    if (draggedIndex === targetIndex) return;

    const newItems = [...items];
    const [draggedItemData] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItemData);

    if (type === 'badge') {
      setBadges(newItems);
      const newBadgeOrder = newItems.map(b => b.badgeType || b.type || b.id);
      console.log('BadgeSettings - New badge order:', newBadgeOrder, 'from items:', newItems);
      setDisplaySettings(prev => ({
        ...prev,
        badgeOrder: newBadgeOrder
      }));
    } else if (type === 'achievement') {
      setAchievements(newItems);
      setDisplaySettings(prev => ({
        ...prev,
        achievementOrder: newItems.map(a => a.id)
      }));
    } else {
      setTitles(newItems);
      setDisplaySettings(prev => ({
        ...prev,
        titleOrder: newItems.map(t => t.type)
      }));
    }

    setDraggedItem(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.mode === 'dark'
            ? 'rgba(18, 18, 18, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 193, 7, 0.3)'
            : '1px solid rgba(25, 118, 210, 0.3)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(255, 193, 7, 0.2)'
            : '0 8px 32px rgba(25, 118, 210, 0.2)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(0, 0, 0, 0.1))'
          : 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(255, 255, 255, 0.1))',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        Profile Customization
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, rgba(255, 193, 7, 0.8), rgba(255, 193, 7, 1))'
                : 'linear-gradient(90deg, rgba(25, 118, 210, 0.8), rgba(25, 118, 210, 1))',
            }
          }}
        >
          <Tab icon={<EmojiEvents />} label="Badges" />
          <Tab icon={<EmojiEvents />} label="Achievements" />
          <Tab icon={<Title />} label="Titles" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={displaySettings.showBadges}
                      onChange={(e) => setDisplaySettings(prev => ({ ...prev, showBadges: e.target.checked }))}
                    />
                  }
                  label="Show badges on profile"
                />
              </Box>

              {displaySettings.showBadges && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Your Badges
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Toggle visibility and drag to reorder your badges
                  </Typography>

                  <List>
                    {badges.map((badge, index) => (
                      <ListItem 
                        key={badge.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, badge, 'badge')}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index, 'badge')}
                        sx={{ 
                          border: '1px solid', 
                          borderColor: 'divider', 
                          borderRadius: 2, 
                          mb: 1,
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 193, 7, 0.08)'
                            : 'rgba(25, 118, 210, 0.08)',
                          backdropFilter: 'blur(10px)',
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' }
                        }}
                      >
                        <IconButton size="small" sx={{ mr: 1, cursor: 'grab' }}>
                          <DragIndicator />
                        </IconButton>
                        <Chip
                          avatar={<Avatar sx={{ bgcolor: badge.color, width: 24, height: 24 }}>{badge.name?.[0]}</Avatar>}
                          label={badge.name}
                          size="small"
                          sx={{ mr: 2 }}
                        />
                        <ListItemText
                          primary={badge.name}
                          secondary={badge.description}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => toggleBadgeVisibility(badge.badgeType || badge.type)}
                            color={displaySettings.visibleBadges.includes(badge.badgeType || badge.type) ? 'primary' : 'default'}
                          >
                            {displaySettings.visibleBadges.includes(badge.badgeType || badge.type) ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}

          {activeTab === 1 && (
            <>
              <Typography variant="h6" gutterBottom>
                Your Achievements
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag to reorder your achievements display
              </Typography>
              <List>
                {achievements.map((achievement, index) => (
                  <ListItem 
                    key={achievement.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, achievement, 'achievement')}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index, 'achievement')}
                    sx={{ 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2, 
                      mb: 1,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.05)'
                        : 'rgba(25, 118, 210, 0.05)',
                      backdropFilter: 'blur(10px)',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' }
                    }}
                  >
                    <IconButton size="small" sx={{ mr: 1, cursor: 'grab' }}>
                      <DragIndicator />
                    </IconButton>
                    <EmojiEvents sx={{ 
                      mr: 2, 
                      color: achievement.unlocked 
                        ? (theme.palette.mode === 'dark' ? '#FFD700' : '#FFA000')
                        : 'grey.500' 
                    }} />
                    <ListItemText
                      primary={achievement.achievementType}
                      secondary={`Progress: ${achievement.progress || 0}% ${achievement.unlocked ? '(Unlocked)' : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        color={achievement.unlocked ? 'primary' : 'default'}
                        disabled={!achievement.unlocked}
                      >
                        {achievement.unlocked ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {activeTab === 2 && (
            <>
              <Typography variant="h6" gutterBottom>
                Your Titles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Manage your title display order
              </Typography>
              <List>
                {titles.map((title, index) => (
                  <ListItem 
                    key={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, title, 'title')}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index, 'title')}
                    sx={{ 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2, 
                      mb: 1,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.05)'
                        : 'rgba(25, 118, 210, 0.05)',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' }
                    }}
                  >
                    <IconButton size="small" sx={{ mr: 1, cursor: 'grab' }}>
                      <DragIndicator />
                    </IconButton>
                    <Title sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={title.name}
                      secondary="User title"
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeSettings;